import React from 'react';
import {ScrollView, View, ViewProps} from 'react-native';

import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import Text from '/component/base/Text';
import TextInput from '/component/base/TextInput';
import TouchableOpacity from '/component/base/TouchableOpacity';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import {acceptDeclineReasonTestIDs} from '/constant/TestIDs';
import {OTHER_REASON_CODE} from '/enum/candidates.enum';
import {withTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {useDeclineReasonPopover} from './DeclineReasonPopover.hook';
import {styles} from './DeclineReasonPopover.style';
import {moveArrayElementToEnd} from './DeclineReasonPopover.util';

export type reasonCodes = {
  reasonTypeId: string;
  reasonDescription: string;
  isSelected?: boolean | false;
};

interface DeclineReasonPopoverProps {
  popoverClose: () => void;
  declineReasonCodes: reasonCodes[];
  onSubmit: (declineReason: any) => void;
  hasDeclineReasons: boolean;
  tt: (keyPrefix: any, value: any) => void;
}

export const DeclineReasonPopover = (props: DeclineReasonPopoverProps) => {
  const {tt, onSubmit, popoverClose, declineReasonCodes} = props;

  const {
    otherReasonSelected,
    otherReasonInput,
    declineReasons,
    handleReasonSelect,
    handleOtherReasonInputChange,
    handleBackgroundColorChange,
  } = useDeclineReasonPopover({
    declineReasonCodes,
  });
  const sortedDeclineReasons = moveArrayElementToEnd(
    declineReasons,
    OTHER_REASON_CODE,
  );

  return (
    <KeyboardAvoidingView style={styles.keyboardViewContainer}>
      <ScrollView keyboardShouldPersistTaps="always">
        <BasePopover style={{base: {...styles.modalView, height: 455}}} padded>
          <View style={styles.modalBody as Readonly<ViewProps>}>
            <View style={styles.headerContainer}>
              <TouchableOpacity
                onPress={popoverClose}
                {...testProperties(null, true)}>
                <Icon name="close" style={styles.closeIcon} />
              </TouchableOpacity>
              <Text
                variant="button"
                style={styles.headerText}
                {...testProperties(
                  acceptDeclineReasonTestIDs.declineReasonTitle,
                )}>
                {tt('confirmation', 'Reason for declining:')}
              </Text>
            </View>
            <View style={styles.inputContainer}>
              {sortedDeclineReasons?.map(
                ({reasonTypeId, reasonDescription}, index) => (
                  <TextInput
                    value={reasonDescription}
                    style={{
                      ...styles.inputText,
                      backgroundColor:
                        handleBackgroundColorChange(reasonTypeId),
                    }}
                    readOnly={true}
                    selectionColor={'transparent'}
                    onFocus={() => handleReasonSelect(reasonTypeId)}
                    showSoftInputOnFocus={false}
                    key={index}
                    {...testProperties(
                      acceptDeclineReasonTestIDs.declineReasonType +
                        reasonTypeId,
                    )}
                  />
                ),
              )}
              {otherReasonSelected && (
                <TextInput
                  style={styles.inputText}
                  placeholder={'input text...'}
                  value={otherReasonInput}
                  maxLength={250}
                  autoFocus={true}
                  onChangeText={handleOtherReasonInputChange}
                  showSoftInputOnFocus={true}
                  {...testProperties(
                    acceptDeclineReasonTestIDs.declineReasonOtherReasonInput,
                  )}
                />
              )}
            </View>
          </View>
          <View style={styles.modalFooter}>
            <Button
              onPress={() =>
                onSubmit(
                  sortedDeclineReasons?.find(
                    (reason: any) => reason?.isSelected,
                  ),
                )
              }
              style={{
                base: styles.buttonSave,
              }}
              disabled={
                !sortedDeclineReasons?.some((reason: any) => reason?.isSelected)
              }
              testID={acceptDeclineReasonTestIDs.declineReasonSave}>
              Save
            </Button>
            <Button
              type="textLink"
              onPress={popoverClose}
              style={styles.buttonCancel}
              testID={acceptDeclineReasonTestIDs.declineReasonCancel}>
              Cancel
            </Button>
          </View>
        </BasePopover>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default withTranslation('DeclineReasonPopoverModal')(
  DeclineReasonPopover,
);
